import { useEffect, useReducer, useCallback, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { PuzzleGrid } from '../components/Grid'
import { CluePanel, CurrentClue } from '../components/CluePanel'
import { Capacitor } from '@capacitor/core'

import { Footer } from '../components/Footer'
import { AboutModal } from '../components/AboutModal'
import type { GameState, Puzzle, Clue } from '../types/puzzle'
import {
  createEmptyGrid,
  isBlockedCell,
  checkAllCorrect,
  getNextCell,
  formatTime,
  getDayNumber,
  getDailyPuzzle,
  recordGameResult
} from '../utils/puzzleUtils'
import { playVictorySound, unlockAudioContext } from '../utils/audio'
import { unlockAchievement, evaluateVictoryAchievements } from '../lib/achievements'

// 示例谜题数据（后续从服务器加载）


/**
 * 游戏状态初始值
 */
const initialState: GameState = {
  puzzle: null,
  userInput: [],
  currentCell: [0, 0],
  direction: 'across',
  startTime: 0,
  endTime: null,
  isComplete: false,
  mistakes: 0,
  hintsUsed: 0,
  resetUsed: 0,
  freeHintsRemaining: 99 // v1.0: Unlimited hints
}

type GameAction =
  | { type: 'SET_PUZZLE'; puzzle: Puzzle }
  | { type: 'INPUT_LETTER'; letter: string }
  | { type: 'DELETE_LETTER' }
  | { type: 'SELECT_CELL'; row: number; col: number }
  | { type: 'TOGGLE_DIRECTION' }
  | { type: 'MOVE_CELL'; moveDirection: 'up' | 'down' | 'left' | 'right' }
  | { type: 'USE_HINT' }
  | { type: 'COMPLETE' }
  | { type: 'RESET' }
  | { type: 'DEBUG_FILL' }

/**
 * 游戏状态 reducer
 */
function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'SET_PUZZLE': {
      const puzzle = action.puzzle
      return {
        ...state,
        puzzle,
        userInput: createEmptyGrid(puzzle.grid.size),
        currentCell: [0, 0],
        direction: 'across',
        startTime: Date.now(),
        endTime: null,
        isComplete: false,
        mistakes: 0,
        hintsUsed: 0
      }
    }

    case 'INPUT_LETTER': {
      if (!state.puzzle || state.isComplete) return state

      const [row, col] = state.currentCell
      const { blocked } = state.puzzle.grid

      // 黑格不能输入
      if (isBlockedCell(blocked, row, col)) return state

      const newInput = state.userInput.map(r => [...r])
      newInput[row][col] = action.letter.toUpperCase()

      // 检查是否完成
      const isComplete = checkAllCorrect(newInput, state.puzzle.grid.cells, blocked)

      // 移动到下一格
      const nextCell = getNextCell(state.currentCell, state.direction, state.puzzle)

      return {
        ...state,
        userInput: newInput,
        currentCell: nextCell,
        isComplete,
        endTime: isComplete ? Date.now() : null
      }
    }

    case 'DELETE_LETTER': {
      if (!state.puzzle || state.isComplete) return state

      const [row, col] = state.currentCell
      const newInput = state.userInput.map(r => [...r])

      // 如果当前格有内容，删除它
      if (newInput[row][col]) {
        newInput[row][col] = ''
      } else {
        // 否则移动到上一格并删除
        const { direction } = state
        let prevRow = row
        let prevCol = col

        if (direction === 'across') {
          prevCol = Math.max(0, col - 1)
        } else {
          prevRow = Math.max(0, row - 1)
        }

        newInput[prevRow][prevCol] = ''
        return {
          ...state,
          userInput: newInput,
          currentCell: [prevRow, prevCol]
        }
      }

      return {
        ...state,
        userInput: newInput
      }
    }

    case 'SELECT_CELL': {
      if (!state.puzzle) return state

      const { row, col } = action
      const { blocked } = state.puzzle.grid

      if (isBlockedCell(blocked, row, col)) return state

      // 如果点击当前选中的格子，切换方向
      const [currentRow, currentCol] = state.currentCell
      if (row === currentRow && col === currentCol) {
        return {
          ...state,
          direction: state.direction === 'across' ? 'down' : 'across'
        }
      }

      return {
        ...state,
        currentCell: [row, col]
      }
    }

    case 'TOGGLE_DIRECTION': {
      return {
        ...state,
        direction: state.direction === 'across' ? 'down' : 'across'
      }
    }

    case 'MOVE_CELL': {
      if (!state.puzzle) return state

      const [row, col] = state.currentCell
      const { size, blocked } = state.puzzle.grid
      let newRow = row
      let newCol = col

      switch (action.moveDirection) {
        case 'up':
          newRow = Math.max(0, row - 1)
          break
        case 'down':
          newRow = Math.min(size - 1, row + 1)
          break
        case 'left':
          newCol = Math.max(0, col - 1)
          break
        case 'right':
          newCol = Math.min(size - 1, col + 1)
          break
      }

      // 跳过黑格
      if (isBlockedCell(blocked, newRow, newCol)) {
        return state
      }

      return {
        ...state,
        currentCell: [newRow, newCol]
      }
    }

    case 'USE_HINT': {
      if (!state.puzzle || state.isComplete) return state

      const [row, col] = state.currentCell
      const { cells, blocked } = state.puzzle.grid

      if (isBlockedCell(blocked, row, col)) return state

      // 检查当前格子是否已经填写了正确答案
      const isAlreadyCorrect = state.userInput[row][col].toUpperCase() === cells[row][col].toUpperCase()

      const newInput = state.userInput.map(r => [...r])
      newInput[row][col] = cells[row][col]

      const isComplete = checkAllCorrect(newInput, cells, blocked)

      return {
        ...state,
        userInput: newInput,
        // 只有在格子之前不正确时才消耗 hint
        hintsUsed: isAlreadyCorrect ? state.hintsUsed : state.hintsUsed + 1,
        freeHintsRemaining: isAlreadyCorrect ? state.freeHintsRemaining : Math.max(0, state.freeHintsRemaining - 1),
        isComplete,
        endTime: isComplete ? Date.now() : null
      }
    }

    case 'RESET': {
      if (!state.puzzle) return state

      return {
        ...state,
        userInput: createEmptyGrid(state.puzzle.grid.size),
        currentCell: [0, 0],
        direction: 'across',
        startTime: Date.now(),
        endTime: null,
        isComplete: false,
        mistakes: 0,
        hintsUsed: 0,
        resetUsed: state.resetUsed + 1,
        freeHintsRemaining: 99 // Reset unlimited
      }
    }

    case 'DEBUG_FILL': {
      if (!state.puzzle) return state

      // 自动填充所有正确答案
      const { cells } = state.puzzle.grid

      return {
        ...state,
        userInput: cells.map(row => [...row]),
        isComplete: true,
        endTime: Date.now()
      }
    }

    default:
      return state
  }
}

/**
 * 每日谜题页面
 */
export function DailyPage() {
  const { t } = useTranslation()
  const [state, dispatch] = useReducer(gameReducer, initialState)
  const [showGameOver, setShowGameOver] = useState(false)
  const [showVictory, setShowVictory] = useState(false)

  const [lastBeepSecond, setLastBeepSecond] = useState<number | null>(null)
  const [isSoundEnabled, setIsSoundEnabled] = useState(true) // 音效开关
  const [remainingTime, setRemainingTime] = useState(60000) // 60秒倒计时（毫秒）

  // Mobile Keyboard Support
  const inputRef = useRef<HTMLInputElement>(null)
  const [hiddenInputValue, setHiddenInputValue] = useState(' ') // Default space for backspace detection
  const lastInputTime = useRef(0)

  // 成就系统：保证「记录本局结果 + 判定成就」这件事每局最多执行一次
  // （胜利和超时失败二选一，谁先发生就记一次）
  const hasRecordedResultRef = useRef(false)

  const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const now = Date.now()
    const val = e.target.value

    // Backspace detected
    if (val.length < 1) {
      dispatch({ type: 'DELETE_LETTER' })
      setHiddenInputValue(' ')
      return
    }

    // Character input
    if (val.length > 1) {
      // Debounce rapid duplicate events (only for insertions)
      // If the same input fires twice in < 50ms, ignore the second one.
      if (now - lastInputTime.current < 50) {
        return
      }
      lastInputTime.current = now

      const char = val.slice(-1)
      if (/^[a-zA-Z]$/.test(char)) {
        dispatch({ type: 'INPUT_LETTER', letter: char.toUpperCase() })
      }
      setHiddenInputValue(' ')
    }
  }

  // 加载谜题
  useEffect(() => {
    // 优先从 URL 获取 day 参数
    const params = new URLSearchParams(window.location.search)
    const urlDay = params.get('day')

    // 如果有参数则使用参数，否则获取随机/今日编号
    const dayToLoad = urlDay ? parseInt(urlDay, 10) : getDayNumber()

    // 从工具函数获取谜题
    const dailyPuzzle = getDailyPuzzle(dayToLoad)
    dispatch({ type: 'SET_PUZZLE', puzzle: dailyPuzzle })
  }, [])

  // 成就：First Spark（首次填入正确字母）/ Grid Filled（首次填满所有格子）/
  // Word Aflame（首次填对一个完整单词）
  // unlockAchievement 内部已做去重，这里只需要在每次输入变化时检查“条件当前是否成立”
  useEffect(() => {
    if (!state.puzzle) return

    const { cells, blocked, size } = state.puzzle.grid
    let hasCorrectCell = false
    let allFilled = true

    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (isBlockedCell(blocked, r, c)) continue
        const val = state.userInput[r][c]
        if (!val) {
          allFilled = false
        } else if (val.toUpperCase() === cells[r][c].toUpperCase()) {
          hasCorrectCell = true
        }
      }
    }

    if (hasCorrectCell) unlockAchievement('first-spark')
    if (allFilled) unlockAchievement('grid-filled')

    const isClueComplete = (clue: Clue, direction: 'across' | 'down') => {
      if (!clue.startCell) return false
      const [startRow, startCol] = clue.startCell
      const len = clue.answer.length
      for (let i = 0; i < len; i++) {
        const r = direction === 'across' ? startRow : startRow + i
        const c = direction === 'across' ? startCol + i : startCol
        if (state.userInput[r]?.[c]?.toUpperCase() !== clue.answer[i]?.toUpperCase()) return false
      }
      return true
    }

    const hasCompleteWord =
      state.puzzle.clues.across.some(clue => isClueComplete(clue, 'across')) ||
      state.puzzle.clues.down.some(clue => isClueComplete(clue, 'down'))

    if (hasCompleteWord) unlockAchievement('word-aflame')
  }, [state.userInput, state.puzzle])

  // 成就：Clue Seeker（首次使用提示）
  // hintsUsed 只会在 reducer 里真正消耗一次提示（格子此前不正确）时才 +1，
  // 因此在这里用 hintsUsed 是否 > 0 判断，不会被“点击提示按钮但格子已正确”的情况误触发
  useEffect(() => {
    if (state.hintsUsed > 0) unlockAchievement('clue-seeker')
  }, [state.hintsUsed])

  // 播放读秒音效的函数
  const playBeep = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      // 设置音效参数
      oscillator.frequency.value = 800 // 频率 (Hz)
      oscillator.type = 'sine' // 正弦波

      // 音量渐变
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1)

      // 播放0.1秒
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.1)
    } catch (error) {
      console.log('Audio playback failed:', error)
    }
  }, [])

  // 倒计时器
  useEffect(() => {
    if (!state.startTime || state.isComplete) return

    const timer = setInterval(() => {
      // If help modal is open, do not count down? 
      // Actually, if we use a Modal, the component doesn't unmount, so state is preserved.
      // But we should PAUSE the timer while help is open?
      // The user just said "don't restart".
      // If we use a Modal, it won't restart. It will just continue or pause.
      // Pausing is a bonus feature.
      // The main request is "do not recalculate" (reset to 60s).
      // So switching to Modal fixes the "Reset" issue.

      const elapsed = Date.now() - state.startTime
      const remaining = Math.max(0, 60000 - elapsed) // 60秒 - 已用时间
      setRemainingTime(remaining)

      // 最后10秒播放读秒音效（仅当音效开启时）
      const secondsLeft = Math.ceil(remaining / 1000)
      if (isSoundEnabled && secondsLeft <= 10 && secondsLeft > 0 && secondsLeft !== lastBeepSecond) {
        playBeep()
        setLastBeepSecond(secondsLeft)
      }

      // 时间到了自动结束游戏并显示游戏结束弹窗
      if (remaining === 0 && !state.isComplete) {
        setShowGameOver(true)
        // 成就：记录本局为「超时失败」，供 from-the-ashes 隐藏成就在下一局判定
        if (!hasRecordedResultRef.current) {
          hasRecordedResultRef.current = true
          recordGameResult({ won: false, durationMs: 60000, hintsUsed: state.hintsUsed })
        }
        dispatch({ type: 'COMPLETE' })
      }
    }, 100) // 更频繁更新以获得更平滑的倒计时

    return () => clearInterval(timer)
  }, [state.startTime, state.isComplete, lastBeepSecond, playBeep, isSoundEnabled])

  // 完成时显示胜利弹窗
  useEffect(() => {
    if (state.isComplete && remainingTime > 0 && !showVictory) {
      // 成就：记录本局为「胜利」，并判定所有累计类/速度类/连续天数类成就
      // 只在本局第一次进入「已完成」状态时记录一次
      if (!hasRecordedResultRef.current && state.endTime) {
        hasRecordedResultRef.current = true
        const durationMs = state.endTime - state.startTime
        const { stats, hasPriorVictory, cameBackFromTimeout } = recordGameResult({
          won: true,
          durationMs,
          hintsUsed: state.hintsUsed
        })
        evaluateVictoryAchievements({
          durationMs,
          hintsUsed: state.hintsUsed,
          stats,
          hasPriorVictory,
          cameBackFromTimeout
        })
      }

      // 在时间内完成，显示胜利弹窗
      setTimeout(() => {
        setShowVictory(true)
        if (isSoundEnabled) playVictorySound()
      }, 500)
    }
  }, [state.isComplete, remainingTime, isSoundEnabled, showVictory, state.endTime, state.startTime, state.hintsUsed])

  // 键盘事件处理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // If typing in the hidden input, ignore global keydown for letters and backspace
      // to avoid double-processing (onChange vs keydown).
      if (e.target === inputRef.current) {
        if (/^[a-zA-Z]$/.test(e.key) || e.key === 'Backspace') {
          return
        }
      }

      // 字母输入
      if (/^[a-zA-Z]$/.test(e.key)) {
        dispatch({ type: 'INPUT_LETTER', letter: e.key })
        return
      }

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault()
          dispatch({ type: 'MOVE_CELL', moveDirection: 'up' })
          break
        case 'ArrowDown':
          e.preventDefault()
          dispatch({ type: 'MOVE_CELL', moveDirection: 'down' })
          break
        case 'ArrowLeft':
          e.preventDefault()
          dispatch({ type: 'MOVE_CELL', moveDirection: 'left' })
          break
        case 'ArrowRight':
          e.preventDefault()
          dispatch({ type: 'MOVE_CELL', moveDirection: 'right' })
          break
        case 'Backspace':
          e.preventDefault()
          dispatch({ type: 'DELETE_LETTER' })
          break
        case ' ':
        case 'Tab':
          e.preventDefault()
          dispatch({ type: 'TOGGLE_DIRECTION' })
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  /**
   * 处理单元格点击
   */
  const handleCellClick = useCallback((row: number, col: number) => {
    dispatch({ type: 'SELECT_CELL', row, col })
    // Focus hidden input for mobile keyboard
    inputRef.current?.focus()
  }, [])

  // iOS Audio Unlock Hook
  useEffect(() => {
    const handleUnlock = () => {
      unlockAudioContext()
      // Remove listeners once unlocked
      document.removeEventListener('click', handleUnlock)
      document.removeEventListener('touchstart', handleUnlock)
      document.removeEventListener('keydown', handleUnlock)
    }

    document.addEventListener('click', handleUnlock)
    document.addEventListener('touchstart', handleUnlock)
    document.addEventListener('keydown', handleUnlock)

    return () => {
      document.removeEventListener('click', handleUnlock)
      document.removeEventListener('touchstart', handleUnlock)
      document.removeEventListener('keydown', handleUnlock)
    }
  }, [])

  /**
   * 处理线索点击
   */
  const handleClueClick = useCallback((clue: Clue, direction: 'across' | 'down') => {
    if (clue.startCell) {
      dispatch({ type: 'SELECT_CELL', row: clue.startCell[0], col: clue.startCell[1] })
    }
    if (state.direction !== direction) {
      dispatch({ type: 'TOGGLE_DIRECTION' })
    }
    // Focus hidden input for mobile keyboard
    inputRef.current?.focus()
  }, [state.direction])

  /**
   * 获取当前活跃的线索
   */
  const getCurrentClue = useCallback((): Clue | null => {
    if (!state.puzzle) return null

    const [row, col] = state.currentCell
    const { clues } = state.puzzle

    if (state.direction === 'across') {
      return clues.across.find(c => c.row === row) || null
    } else {
      return clues.down.find(c => c.col === col) || null
    }
  }, [state.puzzle, state.currentCell, state.direction])

  // About Modal state
  const [showAbout, setShowAbout] = useState(false)

  // Help Modal state (for mobile)
  const [showHelp, setShowHelp] = useState(false)


  // Loading state
  if (!state.puzzle) {
    return (
      <div className="h-dvh flex items-center justify-center">
        <div className="text-white">{t('game.loading')}</div>
      </div>
    )
  }

  // Timer Component for Navbar with color changes
  const getTimerColor = () => {
    if (state.isComplete) return 'text-green-500'
    const seconds = Math.ceil(remainingTime / 1000)
    if (seconds <= 10) return 'text-red-500' // 最后10秒红色
    if (seconds <= 20) return 'text-yellow-500' // 最后20秒黄色
    return 'text-white' // 正常白色
  }

  const TimerDisplay = (
    <div className={`text-2xl font-mono font-bold ${getTimerColor()}`}>
      {state.isComplete
        ? formatTime(state.endTime! - state.startTime)
        : formatTime(remainingTime)
      }
    </div>
  )

  const isNative = Capacitor.isNativePlatform()

  const handleBackgroundClick = (e: React.MouseEvent) => {
    // Ignore clicks on interactive elements (buttons, grid cells, clues)
    const target = e.target as HTMLElement
    if (target.closest('button, .grid-cell, .clue-panel, .current-clue')) return
    inputRef.current?.blur()
  }

  return (
    <div className="h-dvh overflow-hidden flex flex-col bg-zinc-950" onClick={handleBackgroundClick}>
      {/* 主内容：整页不滚动（原生 App 观感），内容超出可用高度时只在这块区域内部滚动 */}
      <main className={`flex-1 min-h-0 overflow-y-auto p-4 flex flex-col items-center justify-center ${isNative ? 'pt-safe pb-safe' : ''}`}>
        <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col justify-center">

          {/* Top Line: Hint + Timer */}
          <div className="flex items-center gap-4 mb-2 w-full">
            <div className="flex-1">
              <CurrentClue clue={getCurrentClue()} direction={state.direction} />
            </div>
            {TimerDisplay}
          </div>

          {/* 游戏区域 */}
          <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
            {/* 网格 */}
            <div className="flex justify-center">
              <PuzzleGrid
                puzzle={state.puzzle}
                userInput={state.userInput}
                currentCell={state.currentCell}
                direction={state.direction}
                isComplete={state.isComplete}
                onCellClick={handleCellClick}
              />
            </div>

            {/* 线索面板 */}
            <div className="hidden lg:block">
              <CluePanel
                acrossClues={state.puzzle.clues.across}
                downClues={state.puzzle.clues.down}
                activeClueNumber={getCurrentClue()?.number}
                direction={state.direction}
                onClueClick={handleClueClick}
              />
            </div>
          </div>

          {/* 移动端线索面板 */}
          <div className="mt-6 lg:hidden">
            {/* 移动端：按钮在提示区上方 */}
            <div className="mb-4 flex justify-center gap-2 flex-wrap">
              <button
                onClick={() => dispatch({ type: 'USE_HINT' })}
                className="btn btn-secondary flex-1 min-w-[100px] text-sm"
                disabled={state.isComplete}
              >
                💡 {t('game.hint')}
              </button>
              <button
                onClick={() => dispatch({ type: 'RESET' })}
                className={`btn btn-secondary flex-1 min-w-[100px] text-sm ${state.resetUsed >= 1 || remainingTime <= 20000 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                disabled={state.resetUsed >= 1 || remainingTime <= 20000}
                title={remainingTime <= 20000 ? '最后20秒不能重置' : ''}
              >
                🔄 {t('game.reset')} ({Math.max(0, 1 - state.resetUsed)})
              </button>
              <button
                onClick={() => setIsSoundEnabled(!isSoundEnabled)}
                className="btn btn-secondary flex-1 min-w-[100px] text-sm"
                title={isSoundEnabled ? t('game.sound_on') : t('game.sound_off')}
              >
                {isSoundEnabled ? '🔊' : '🔇'} {isSoundEnabled ? t('game.sound_on') : t('game.sound_off')}
              </button>
            </div>

            <CluePanel
              acrossClues={state.puzzle.clues.across}
              downClues={state.puzzle.clues.down}
              activeClueNumber={getCurrentClue()?.number}
              direction={state.direction}
              onClueClick={handleClueClick}
            />
            <p className="text-zinc-500 text-xs text-center mt-4 pb-8">
              Tap blank area to close keyboard
            </p>
          </div>

          {/* 桌面端操作按钮 */}
          <div className="mt-8 hidden lg:flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => dispatch({ type: 'USE_HINT' })}
              className="btn btn-secondary min-w-[120px]"
              disabled={state.isComplete}
            >
              💡 {t('game.hint')}
            </button>
            <button
              onClick={() => dispatch({ type: 'RESET' })}
              className={`btn btn-secondary min-w-[120px] ${state.resetUsed >= 1 || remainingTime <= 20000 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              disabled={state.resetUsed >= 1 || remainingTime <= 20000}
              title={remainingTime <= 20000 ? '最后20秒不能重置' : ''}
            >
              🔄 {t('game.reset')} ({Math.max(0, 1 - state.resetUsed)})
            </button>
            <button
              onClick={() => setIsSoundEnabled(!isSoundEnabled)}
              className="btn btn-secondary min-w-[120px]"
              title={isSoundEnabled ? t('game.sound_on') : t('game.sound_off')}
            >
              {isSoundEnabled ? '🔊' : '🔇'} {isSoundEnabled ? t('game.sound_on') : t('game.sound_off')}
            </button>
          </div>
        </div>
      </main>

      <Footer onAboutClick={() => setShowAbout(true)} onHelpClick={() => setShowHelp(true)} />

      {/* About Modal */}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {/* Help Modal (Mobile) */}
      {
        showHelp && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-zinc-900 border-2 border-brand-orange rounded-2xl p-6 max-w-2xl w-full my-8">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                  <span className="text-3xl">❓</span>
                  {t('help.title')}
                </h1>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-zinc-400 hover:text-white text-2xl w-8 h-8 flex items-center justify-center"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6 text-zinc-300">
                <section>
                  <h2 className="text-xl font-bold text-brand-orange mb-3 border-b border-zinc-800 pb-2">
                    {t('help.intro_title')}
                  </h2>
                  <p className="leading-relaxed whitespace-pre-line">
                    {t('help.intro_desc')}
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-brand-orange mb-3 border-b border-zinc-800 pb-2">
                    {t('help.objective')}
                  </h2>
                  <p className="leading-relaxed">
                    {t('help.objective_desc')}
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-brand-orange mb-3 border-b border-zinc-800 pb-2">
                    {t('help.tools_title')}
                  </h2>
                  <ul className="space-y-2">
                    <li className="flex gap-2">
                      <span className="text-brand-red font-bold">•</span>
                      {t('help.hint_rule')}
                    </li>
                    <li className="flex gap-2">
                      <span className="text-brand-red font-bold">•</span>
                      {t('help.reset_rule')}
                    </li>
                  </ul>
                </section>
              </div>
            </div>
          </div>
        )
      }

      {/* Game Over Modal */}
      {
        showGameOver && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border-2 border-red-500 rounded-2xl p-8 max-w-md w-full animate-bounce-in">
              <div className="text-center">
                <div className="text-6xl mb-4">⏰</div>
                <h2 className="text-3xl font-bold text-red-500 mb-2">
                  {t('game.time_up')}
                </h2>
                <p className="text-zinc-400 mb-6">
                  {t('game.time_up_message')}
                </p>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      // 生成新的随机谜题编号并立即导航（不保留历史记录）
                      const newDay = Math.floor(Math.random() * 10000) + 1
                      window.location.replace(`/daily?day=${newDay}`)
                    }}
                    className="btn btn-primary w-full"
                  >
                    🔄 {t('game.play_again')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      <input
        ref={inputRef}
        type="text"
        maxLength={2}
        value={hiddenInputValue}
        onChange={handleHiddenInputChange}
        onKeyDown={(e) => {
          if (e.key === ' ') {
            e.preventDefault()
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            inputRef.current?.blur()
          }
        }}
        className="opacity-0 absolute top-0 left-0 h-1 w-1 -z-10 overflow-hidden pointer-events-none"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck="false"
        inputMode="text"
      />

      {/* Victory Modal */}
      {
        showVictory && state.puzzle && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-hidden">
            {/* Fireworks animation background */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="firework" style={{ left: '20%', animationDelay: '0s' }}></div>
              <div className="firework" style={{ left: '50%', animationDelay: '0.5s' }}></div>
              <div className="firework" style={{ left: '80%', animationDelay: '1s' }}></div>
              <div className="firework" style={{ left: '35%', animationDelay: '1.5s' }}></div>
              <div className="firework" style={{ left: '65%', animationDelay: '2s' }}></div>
            </div>

            <div className="bg-gradient-to-br from-orange-900/95 to-red-900/95 border-4 border-yellow-500 rounded-2xl p-6 sm:p-8 max-w-md w-full animate-bounce-in relative overflow-hidden shadow-2xl">
              {/* Flame decorations */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 animate-pulse"></div>

              <div className="text-center relative z-10">
                <div className="text-6xl sm:text-7xl mb-4 animate-bounce">🔥</div>
                <h2 className="text-3xl sm:text-4xl font-bold text-yellow-300 mb-2 drop-shadow-lg">
                  {t('game.victory')}
                </h2>
                <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4">
                  {t('game.congratulations')}
                </h3>
                <p className="text-yellow-100 mb-4 text-sm sm:text-base">
                  {t('game.victory_message')}
                </p>

                <div className="bg-black/30 rounded-lg p-4 mb-6">
                  <p className="text-yellow-300 font-semibold mb-2">{t('game.your_time')}</p>
                  <p className="text-4xl sm:text-5xl font-bold text-white font-mono">
                    {formatTime(state.endTime! - state.startTime)}
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      const newDay = Math.floor(Math.random() * 10000) + 1
                      window.location.replace(`/daily?day=${newDay}`)
                    }}
                    className="btn btn-primary w-full text-base sm:text-lg py-3 sm:py-4"
                  >
                    🔄 {t('game.play_again')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  )
}
